import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { AlertCircle } from 'lucide-react'
import { getMeta } from '@/api/meta'
import { Banner } from '@/components/Banner'
import { InfoTooltip } from '@/components/InfoTooltip'
import { useAuthToken } from '@/hooks/useAuthToken'
import { errorMessage } from '@/lib/apiErrors'
import { CopyButton } from './components/CopyButton'

function SkeletonBlock() {
  return <div className="h-32 animate-pulse rounded-control bg-surface-raised" />
}

export function SettingsScreen() {
  const { email, clearSession } = useAuthToken()
  const navigate = useNavigate()
  const metaQuery = useQuery({ queryKey: ['meta'], queryFn: getMeta })

  function handleSignOut() {
    clearSession()
    navigate('/access')
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-title font-semibold text-text-primary">Settings</h1>

      {metaQuery.isPending && <SkeletonBlock />}

      {metaQuery.isError && (
        <Banner variant="danger" icon={AlertCircle} title="Failed to load storage settings">
          {errorMessage(metaQuery.error)}
        </Banner>
      )}

      {metaQuery.isSuccess && (
        <section className="flex flex-col gap-2 rounded-control border border-border bg-surface p-4">
          <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
            Storage
            <InfoTooltip text="The cloud storage location where the platform's configuration files are kept." />
          </h2>
          <div className="flex items-center gap-3">
            <span className="font-mono text-body text-text-primary">{metaQuery.data.configBucketName}</span>
            <CopyButton value={metaQuery.data.configBucketName} />
          </div>
        </section>
      )}

      <section className="flex flex-col gap-3 rounded-control border border-border bg-surface p-4">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Session
          <InfoTooltip text="Your current login to this admin tool." />
        </h2>
        <div className="flex items-center justify-between">
          <span className="text-body text-text-primary">Signed in as {email}</span>
          <button
            type="button"
            onClick={handleSignOut}
            className="rounded-control border border-border px-4 py-2 text-body font-medium text-text-primary"
          >
            Sign Out
          </button>
        </div>
      </section>
    </div>
  )
}
